// row 114 PersonPick: a person select built as a combobox (useListbox, WAI-ARIA 1.2) — not a Menu, because a text box
// inside role="menu" breaks the menu's content model. The box shows the chosen person as an initials Avatar, the name
// in the input and the seat after it; typing filters the list. The list is a role="listbox" of role="option" rows
// (Avatar · name · seat), portalled under the box (inline under SSR and before mount). ArrowDown/ArrowUp open and move,
// Home/End jump, Enter picks, Escape closes and restores the chosen name, a typed character starts the search from
// itself, a press outside closes. Closed, an empty hidden element keeps aria-controls resolving. Loading: skeleton
// options; empty (no one to pick): "—", read-only, never opens. Inside a Field
// it takes the Field's id, label and wiring; in a lane field, pass aria-labelledby. `preview`: a gallery section's
// static rendering of the open list (no focus moves, no outside listener).

import { useCallback, useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";
import { type ControlProps } from "../core/Button";
import { FIELD_NONE, useFieldControl } from "../core/Field";
import { Icon } from "../core/icons";
import { Avatar } from "../core/Person";
import { Skeleton } from "../core/Skeleton";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { McPortal, usePortalTarget } from "../lib/McRoot";
import { Tooltip } from "../lib/Tooltip";
import { useIsoLayoutEffect } from "../lib/useIsoLayoutEffect";
import { useListbox } from "../lib/useListbox";
import { vars, type McVarValue } from "../lib/vars";

export interface PersonOption {
  value: string;
  /** The person's name (data). */
  name: string;
  /** The seat in his role words (*Interviewer*, *Recruiter* …). */
  seat?: string | undefined;
  /** The ring hue (a --p-* token var); default from the value. */
  hue?: McVarValue | undefined;
  hs?: Hs | undefined;
}

export interface PersonPickProps extends Omit<ControlProps, "hs"> {
  options: readonly PersonOption[];
  value?: string | undefined;
  onChange?: ((value: string) => void) | undefined;
  /** The list's options are not known yet: skeleton rows. */
  loading?: boolean | undefined;
  sm?: boolean | undefined;
  invalid?: boolean | undefined;
  required?: boolean | undefined;
  open?: boolean | undefined;
  defaultOpen?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  preview?: boolean | undefined;
  id?: string | undefined;
  "aria-labelledby"?: string | undefined;
  "aria-describedby"?: string | undefined;
  /** The list's name when no aria-labelledby is given (the field's words: *Interviewer*, *Backup* …). */
  label?: string | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

const fold = (text: string) => text.toLocaleLowerCase();

function Row({ person, props }: { person: PersonOption; props: ReturnType<ReturnType<typeof useListbox>["option"]> }) {
  const hot = useHs(person.hs);
  return (
    <div {...props} {...hot} className={cx("mc-pick-o", props["aria-selected"] && "mc-pick-act")}>
      <Avatar name={person.name} hueKey={person.value} hue={person.hue} size={24} decorative />
      <span className="mc-pick-n">{person.name}</span>
      {person.seat ? <span className="mc-pick-s">{person.seat}</span> : null}
    </div>
  );
}

export function PersonPick({
  options, value, onChange, loading, sm, invalid, required, open, defaultOpen = false, onOpenChange, preview = false, id,
  "aria-labelledby": labelledBy, "aria-describedby": describedBy, label, can, blocked, mode, hs, className,
}: PersonPickProps) {
  const hot = useHs(hs);
  const control = useFieldControl({ id, invalid, blocked, mode, required, "aria-describedby": describedBy });
  const [own, setOwn] = useState(defaultOpen);
  const [query, setQuery] = useState<string | null>(null);
  const empty = options.length === 0 && !loading;
  const isOpen = (open ?? own) && !control.blocked && !empty;
  const chosen = options.find((option) => option.value === value);
  const shown = query === null || query === "" ? options : options.filter((option) => fold(option.name).includes(fold(query)));
  const count = loading ? 0 : shown.length;

  const setOpen = useCallback((next: boolean) => {
    if (open === undefined) setOwn(next);
    onOpenChange?.(next);
    if (!next) setQuery(null);
  }, [open, onOpenChange]);

  const listbox = useListbox({
    count,
    open: isOpen,
    onOpenChange: setOpen,
    onPick: (index) => { const person = shown[index]; if (person) onChange?.(person.value); },
    selected: shown.findIndex((option) => option.value === value),
  });

  // Placed under the box, measured from the layer's origin (as Menu does).
  const target = usePortalTarget();
  const box = useRef<HTMLSpanElement | null>(null);
  const list = useRef<HTMLDivElement | null>(null);
  const [at, setAt] = useState<{ x: number; y: number; w: number } | null>(null);
  const place = useCallback(() => {
    if (!target || !box.current) return;
    const rect = box.current.getBoundingClientRect();
    const layer = target.getBoundingClientRect();
    setAt({ x: Math.round(rect.left - layer.left), y: Math.round(rect.bottom - layer.top), w: Math.round(rect.width) });
  }, [target]);
  useIsoLayoutEffect(() => { if (isOpen) place(); }, [isOpen, place]);
  useEffect(() => {
    if (!isOpen || preview) return;
    const onDown = (event: PointerEvent) => {
      const node = event.target as Node | null;
      if (list.current?.contains(node) || box.current?.contains(node)) return;
      setOpen(false);
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    document.addEventListener("pointerdown", onDown, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      document.removeEventListener("pointerdown", onDown, true);
    };
  }, [isOpen, preview, place, setOpen]);

  if (can === false) return null;
  if (control.mode === "view") {
    return (
      <span className="mc-pick-v">
        {chosen ? <Avatar name={chosen.name} hueKey={chosen.value} hue={chosen.hue} size={24} decorative /> : null}
        <span>{chosen?.name ?? FIELD_NONE}</span>
        {chosen?.seat ? <span className="mc-pick-s">{chosen.seat}</span> : null}
      </span>
    );
  }

  const inert = Boolean(control.blocked);
  const input = (
    <span
      ref={box}
      className={cx("mc-inp", "mc-pick", sm && "mc-inp-sm", control.invalid && "mc-inp-err", inert && "mc-dis", !chosen && "mc-pick-empty", className)}
    >
      {chosen && query === null ? <Avatar name={chosen.name} hueKey={chosen.value} hue={chosen.hue} size={24} decorative /> : null}
      <input
        {...control.aria}
        {...(inert || empty ? {} : listbox.input)}
        {...hot}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listbox.listbox.id}
        aria-autocomplete="list"
        aria-labelledby={labelledBy}
        aria-label={labelledBy === undefined && !control.inField ? label : undefined}
        id={control.id}
        className="mc-inp-c"
        type="text"
        autoComplete="off"
        readOnly={inert || empty}
        value={query ?? chosen?.name ?? (empty ? FIELD_NONE : "")}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          if (inert) return;
          setQuery(event.target.value);
          if (!isOpen) setOpen(true);
        }}
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
          if (inert || empty) return;
          // The first typed character replaces the chosen name: the search starts from it.
          if (query === null && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) event.currentTarget.select();
          listbox.input.onKeyDown(event);
        }}
        onClick={() => { if (!inert && !empty) setOpen(!isOpen); }}
      />
      {chosen?.seat && query === null ? <span className="mc-pick-s">{chosen.seat}</span> : null}
      <Icon name="down" size={16} />
    </span>
  );

  const floating = target !== null && at !== null;
  return (
    <span className="mc-pick-w">
      {inert && control.blocked ? <Tooltip label={control.blocked.reason} mode="mirror">{input}</Tooltip> : input}
      {control.reason}
      <McPortal>
        {isOpen ? (
          <div
            ref={list}
            {...listbox.listbox}
            aria-labelledby={labelledBy ?? (control.labelId !== undefined && label === undefined ? control.labelId : undefined)}
            aria-label={labelledBy === undefined && (label !== undefined || control.labelId === undefined) ? label : undefined}
            aria-busy={loading ? true : undefined}
            className={cx("mc-pick-list", floating && "mc-pick-float")}
            style={floating ? vars({ "--mc-pick-x": at.x, "--mc-pick-y": at.y, "--mc-pick-w": at.w }) : undefined}
          >
            {loading
              ? [0, 1, 2].map((row) => <div key={row} className="mc-pick-o mc-pick-skel" aria-hidden="true"><Skeleton shape="pill" width={24} height={24} /><Skeleton width={120} /></div>)
              : shown.length === 0
                ? <div className="mc-pick-o mc-pick-none" role="option" aria-selected={false} aria-disabled="true">{FIELD_NONE}</div>
                : shown.map((person, index) => <Row key={person.value} person={person} props={listbox.option(index)} />)}
          </div>
        ) : <div id={listbox.listbox.id} className="mc-pick-off" hidden />}
      </McPortal>
    </span>
  );
}
